import { FunctionComponent } from 'react'

import styled, { css } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import { Image } from '@utils/cook/types'
import useArticleSchema from '@hooks/useArticleSchema'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import TextboxEntityImage from '@components/EntityTextbox/TextboxEntityImage'
import TextboxEntityTextWrapper from '@components/EntityTextbox/TextboxEntityTextWrapper'
import TextboxFmTButton from '@components/EntityTextbox/TextboxFmTButton'

type EntityTextBoxAPIProps = {
  title: string
  body?: string
  entityId: string
  image?: Image<'free'>
  imageDark?: Image<'free'>
}

const StyledWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing32 },
    },
  }) => {
    return css`
      margin: ${spacing32} 0;
    `
  }}
`

const StyledContentWrapper = styled.div`
  ${({
    theme: {
      colors: { strokeWeak },
      spacing: { spacing4, spacing32, spacing24, spacing16 },
    },
  }) => css`
    border-radius: ${spacing4};
    border: 1px solid ${strokeWeak};
    display: flex;
    flex-direction: column;
    padding: ${spacing32} ${spacing24};
    gap: ${spacing24};
    ${mobileCSS(css`
      padding: ${spacing24} ${spacing16};
    `)}
  `}
`

const StyledTitle = styled.div`
  ${({
    theme: {
      typography: {
        headings: {
          small: { bundledCSS: smallHeading },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${smallHeading};
    color: ${textDefault};
  `}
`

const EntityTextbox: FunctionComponent<EntityTextBoxAPIProps> = ({
  title,
  body,
  entityId,
  image,
  imageDark,
}) => {
  const textForSchema = `${title ? `<h3>${title}</h3>` : ''}${body}`
  useArticleSchema({ text: textForSchema })

  if (!title && !entityId) {
    return null
  }

  return (
    <StyledWrapper>
      <StyledContentWrapper>
        <StyledTitle>{title}</StyledTitle>
        {image && (
          <TextboxEntityImage imageDark={imageDark}>{image}</TextboxEntityImage>
        )}
        {body && <TextboxEntityTextWrapper>{body}</TextboxEntityTextWrapper>}
        <TextboxFmTButton entityId={entityId} />
      </StyledContentWrapper>
    </StyledWrapper>
  )
}

const widget = {
  kind: ['entity-textbox'],
  component: EntityTextbox,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
